'use client';

import React, { useState } from 'react';
import { Endpoint, EndpointAttributes } from '@context/openapi/types';
import { CodeSample } from '@/services/postmanCollection/postman_util';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@components/components/ui/resizable';
import { RequestSampleContainer } from '@components/container/request-sample-container';
import { ApiContainer } from '@components/container/api-container';
import { ResponseSampleContainer } from '@components/container/response-sample-container';
import { EndpointSelectionBar } from '@components/_components/ui/EndpointSelectionBar';
import { AuthBlurGate } from '@components/ui/Auth/AuthBlurGate';

interface ExplorerPlaygroundProps {
  resource: string;
  endpoint: Endpoint;
  endpointAttributes: EndpointAttributes;
  codeSampleOperation: string;
  codeSampleContents?: string;
}

export const ExplorerPlayground: React.FC<ExplorerPlaygroundProps> = ({
  resource,
  endpoint,
  endpointAttributes,
  codeSampleOperation,
  codeSampleContents,
}) => {
  const [codeSample, setCodeSample] = useState<CodeSample | undefined>();
  const [
    sampleResponseDisclaimerVisibility,
    setSampleResponseDisclaimerVisibility,
  ] = useState<boolean>(false);

  return (
    <AuthBlurGate
      heading="Try Chargebee APIs and inspect responses"
      description="Login to send API requests in the playground. Your Chargebee test site will be used automatically."
    >
      <div
        className={`h-[calc(100vh-52px)] bg-fixed bg-gradient-to-b from-[rgba(255,255,255,1)] to-[rgba(255,255,255,0)] via-[hsl(185,52%,90%)]`}
      >
        <div className="h-[calc(100vh-52px)] bg-fixed bg-gradient-to-l from-[rgba(255,255,255,1)] to-[rgba(255,255,255,0)] via-[hsl(185,60%,95%)]">
          <ResizablePanelGroup
            direction="horizontal"
            className="h-[calc(100vh-52px)] w-full"
          >
            <ResizablePanel defaultSize={60} minSize={40}>
              <div className="flex flex-col h-full">
                <EndpointSelectionBar
                  currentEndpoint={endpoint}
                  resource={resource}
                  apiEndpoint={endpoint}
                />
                <div className="flex flex-1 justify-center py-2 pr-6 w-full overflow-hidden">
                  <ApiContainer
                    resource={resource}
                    apiEndpoint={endpoint}
                    apiEndpointAttributes={endpointAttributes}
                    {...(codeSampleContents && { codeSampleContents })}
                    codeSample={codeSample}
                    setCodeSample={setCodeSample}
                    setSampleResponseDisclaimerVisibility={
                      setSampleResponseDisclaimerVisibility
                    }
                    className="col-span-6"
                  />
                </div>
              </div>
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel defaultSize={40} minSize={30}>
              <ResizablePanelGroup direction="vertical" className="h-full">
                <ResizablePanel
                  defaultSize={50}
                  minSize={20}
                  className="bg-white"
                >
                  <RequestSampleContainer
                    codeSampleOperation={codeSampleOperation}
                    codeSample={codeSample}
                    setSampleResponseDisclaimerVisibility={
                      setSampleResponseDisclaimerVisibility
                    }
                  />
                </ResizablePanel>
                <ResizableHandle />
                <ResizablePanel
                  defaultSize={50}
                  minSize={20}
                  className="bg-white"
                >
                  <ResponseSampleContainer
                    sampleResponseDisclaimerVisibility={
                      sampleResponseDisclaimerVisibility
                    }
                  />
                </ResizablePanel>
              </ResizablePanelGroup>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </div>
    </AuthBlurGate>
  );
};
